import type { FormEvent, MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

type SubmitState = 'idle' | 'loading' | 'success' | 'error';

type SubscribeModalProps = {
  triggerLabel?: string;
  triggerClassName?: string;
  successCloseDelayMs?: number;
};

const WEBHOOK_URL = 'https://n8n.srv1197713.hstgr.cloud/webhook/5013988b-3672-494d-976b-a07a8d53ff3c';

export function SubscribeModal({
  triggerLabel = 'Subscribe',
  triggerClassName = '',
  successCloseDelayMs = 1000
}: SubscribeModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<SubmitState>('idle');
  const [message, setMessage] = useState('');
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const focusableSelector =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
      }

      if (event.key !== 'Tab' || !modalRef.current) return;
      const focusable = Array.from(
        modalRef.current.querySelectorAll<HTMLElement>(focusableSelector)
      ).filter((node) => !node.hasAttribute('disabled'));

      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeElement = document.activeElement as HTMLElement;

      if (event.shiftKey) {
        if (activeElement === first || !modalRef.current.contains(activeElement)) {
          event.preventDefault();
          last.focus();
        }
      } else if (activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const timer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 40);

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.clearTimeout(timer);
      previouslyFocused.current?.focus();
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    setStatus('idle');
    setMessage('');
    setEmail('');
  };

  const handleOverlayClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === overlayRef.current) {
      handleClose();
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!email) return;

    setStatus('loading');
    setMessage('');

    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          source: 'landing_modal',
          user_agent: navigator.userAgent
        })
      });

      if (!response.ok) {
        throw new Error('Request failed');
      }

      setStatus('success');
      setMessage('Thanks for subscribing');

      window.setTimeout(() => {
        handleClose();
      }, successCloseDelayMs);
    } catch (err) {
      console.error('Subscription failed', err);
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    }
  };

  return (
    <>
      <button type="button" className={`subscribe-trigger ${triggerClassName}`} onClick={() => setIsOpen(true)}>
        {triggerLabel}
      </button>

      {isOpen ? (
        <div
          className="subscribe-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="subscribe-modal-title"
          onMouseDown={handleOverlayClick}
          ref={overlayRef}
        >
          <div className="subscribe-modal" ref={modalRef} role="document">
            <button type="button" className="subscribe-close" aria-label="Close subscribe modal" onClick={handleClose}>
              ×
            </button>

            <div className="subscribe-header">
              <p className="eyebrow">Newsletter</p>
              <div className="subscribe-brand" aria-label="QuestFlow">
                <img src="/logo.jpeg" alt="QuestFlow logo" className="subscribe-logo" />
                <h2 id="subscribe-modal-title" className="subscribe-title">
                  <span className="subscribe-wordmark-quest">Quest</span>
                  <span className="subscribe-wordmark-flow">Flow</span>
                </h2>
              </div>
              <p className="subscribe-intro">
                Weekly dispatches on the flows, founders, and tools shaping work. No spam, just signal.
              </p>
            </div>

            <form className="subscribe-form" onSubmit={handleSubmit}>
              <label className="subscribe-label" htmlFor="subscribe-email">
                Email address
              </label>
              <div className="subscribe-row">
                <input
                  ref={inputRef}
                  id="subscribe-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="subscribe-input"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  aria-required="true"
                />
                <button
                  type="submit"
                  className="subscribe-submit"
                  disabled={status === 'loading'}
                  aria-busy={status === 'loading'}
                >
                  {status === 'loading' ? 'Submitting…' : 'Subscribe'}
                </button>
              </div>
              <p className="subscribe-helper">We respect your inbox. Unsubscribe anytime.</p>
              {message ? (
                <p className={`subscribe-status ${status === 'error' ? 'subscribe-status--error' : 'subscribe-status--success'}`} role="status">
                  {message}
                </p>
              ) : null}
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}
